import React, { Fragment, Component } from "react";
import { SiteData, RouteData, Head } from "react-static";
import styled from "styled-components";
import { Centered } from "../../containers/Layout";
import { StickedNavbar, links } from "../../segments/NavbarSegment";
import Footer from "../../segments/FooterSegment";
import { ResponsiveImage } from "../../containers/ProgressiveHelpers";

const Site = styled.div`
  @media (max-width: 1810px) {
    font-size: 10px;
  }

  position: relative;
  min-height: 100vh;

  &:before {
    content: "";
    background-image: url(${props => props.src});
    background-size: cover;
    filter: saturate(0) contrast(2) blur(2.5px) opacity(0.65);
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: -1;
  }

  &:after {
    content: "";
    width: 100%;
    height: 100%;
    background: radial-gradient(rgba(0, 0, 0, 0) 45%, hsl(0, 0%, 100%));
    position: absolute;
    z-index: -1;
    top: 0;
  }
`;

const Paragraph = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 1.5em;
  color: #333333;
  font-weight: 300;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(auto, 57.5em));
  justify-content: space-around;
  font-size: 0.85em;
  position: absolute;
  width: 100%;
  z-index: 100;
  top: 15em;
  left: 0;
  grid-gap: 2.5em;
  padding: .5em;
  box-sizing: border-box;
}
`;

const Headline = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 2.5em;
  color: #333333;
  font-weight: normal;
  padding-top: 1.5em;
  padding-bottom: 1.5em;
  text-align: center;
`;

const Card = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 1.5em;
  background-color: #f4f4f4;
  color: #828282;
  padding: 1.5em;
  border-radius: 1.5em;
  overflow: hidden;
  box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.25);
  line-height: 1.15;

  &:after {
    content: "";
    background: linear-gradient(30deg, rgb(87, 124, 255), rgb(111, 54, 255));
    width: 100%;
    height: 1em;
    box-shadow: 0px 5px 20px 0px rgba(255, 255, 255, 0.44);
  }

  & a {
    color: rgb(150, 95, 192);
    font-weight: 500;
  }
`;

const Title = styled.div`
  font-size: 1.166em;
  color: #333333;
  text-align: center;
  font-weight: bold;
  padding-left: 1em;
  padding-right: 1em;
  line-height: 3;
  box-sizing: border-box;
`;

const Content = styled.div`
  margin: 5em 7.5em 0em 7.5em;
  padding-bottom: 5em;

  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;

  width: 100%;
  box-sizing: border-box;
  margin: 5em 0 0 0;
`;

const Button = styled.a`
  all: initial;
  border-radius: 2.5em;
  border-color: rgba(153, 128, 214, 0.75);
  border-width: 0.275em;
  display: inline-flex;
  background-color: aliceblue;
  height: 2.5em;
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  background: linear-gradient(30deg, rgb(87, 124, 255), rgb(111, 54, 255));
  color: white;
  justify-content: space-around;
  align-items: center;
  padding: 0.5em 2em 0.5em 2em;
  border-style: solid;
  position: relative;
  z-index: 0;
  transition: opacity 1s;
  cursor: pointer;
  transition: transform 0.5s;
  font-size: 0.75em;
  &:after {
    background: linear-gradient(30deg, rgb(111, 54, 255), rgb(87, 124, 255));
    border-radius: 2.5em;
    opacity: 0;
    width: 100%;
    height: 100%;
    content: "";
    position: absolute;
    z-index: -1;
    transition: opacity 0.5s;
  }

  &:hover:after {
    opacity: 1;
  }

  &:hover {
    transform: scale(0.95);
  }

  @media (max-width: 540px) {
    font-size: 1.1em;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 50em;
  min-height: 5em;
  padding: 1.5em;
  font-family: "Raleway", sans-serif;
  font-size: 1.15em;
  background-color: #ffffff;
  color: #828282;
  margin: 1.5em;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.25);
  border-radius: 1.5em;
  font-size: 1.275em;
  text-align: center;

  margin-top: 7.5em;
  padding-top: 20em;

  @media (max-width: 1024px) {
    margin-top: 11.5em;
    padding-top: 42.5em;
    max-width: 25em;
  }

  @media (max-width: 450px) {
    margin-top: 14em;
  }

  @media (max-width: 375px) {
    margin-top: 16em;
  }

  @media (max-width: 355px) {
    margin-top: 18em;
  }

  a {
    color: rgb(150, 95, 192);
    font-weight: 500;
  }
`;

const json = {
  "@context": "https://schema.org/",
  "@type": "JobPosting",
  datePosted: "2019-06-13",
  description: `Wir suchen ab sofort oder später jeweils einen Kollegen bzw. eine Kollegin zur Verstärkung unserer Teams an den Standorten Mannheim-Rheinau und Offenbach an der Queich. In unseren logopädischen Praxen behandeln wir alle Störungsbilder.<br />
<br />
<h3>Mannheim-Rheinau</h3>
   Die Praxis in Mannheim ist sehr gut zu erreichen
  (Straßenbahnhaltestelle in ca. 100 m Entfernung bzw.
  Parkmöglichkeiten in der Nähe).<br />Wir suchen ab sofort oder
  später eine Kollegin in Teil- oder Vollzeit (gerne auch als
  fachliche Leitung).
    <br /><br />Logopädie Palinkas<br />Dänischer Tisch 25<br />68219 Mannheim<br /><br />
 <h3>Offenbach an der Queich</h3>
   Unsere Praxis in Offenbach liegt ca. 6km von Landau entfernt. Auch bei dieser sind immer ausreichend Parkmöglichkeiten in unmittelbarer Nähe vorhanden.<br />Wir suchen ab sofort oder später eine Kollegin für
  10-15 Wochenstunden (später gerne mehr).
  <br /><br />Logopädie Palinkas<br />Schlesienstr. 3<br />76877 Offenbach<br /><br /><br />

  Es erwarten Sie jeweils ein nettes Team, ein
  eigener Therapieraum mit computergestütztem Arbeitsplatz,
  betriebliche Altersvorsorge, Fortbildungszuschuss und eine
  überdurchschnittliche Bezahlung.
<br /><br />
Bitte senden Sie ihre Bewerbung an:<br />
janixis@gmx.de<br />
<br />
Tel.: 0621-8995663
<h4>Wir freuen uns auf Ihre Bewerbung!</h4>
`,
  hiringOrganization: {
    "@type": "Organization",
    name: "Logopädie Palinkas",
    sameAs: "https://logopaedie-palinkas.de/",
    logo:
      "https://logopaedie-palinkas.de/uploads/icons/android-chrome-192x192.png"
  },
  jobLocation: [
    {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Dänischer Tisch 25",
        addressLocality: "Mannheim",
        addressRegion: "Baden-Württemberg",
        postalCode: "68219",
        addressCountry: "DE"
      }
    },
    {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Schlesienstr. 3",
        addressLocality: "Offenbach an der Queich",
        addressRegion: "Rheinland-Pfalz",
        postalCode: "76877 ",
        addressCountry: "DE"
      }
    }
  ],
  title: "Logopäde (m/w/d) zur Verstärkung unserer Teams",
  employmentType: ["FULL_TIME", "PART_TIME"]
};

export default () => (
  <ResponsiveImage path="/uploads/scrabble.jpg">
    <Site>
      <Head>
        <title>Stellenangebote</title>
        <script type="application/ld+json">{JSON.stringify(json)}</script>
        <meta name="title" content="Verstärkung für unsere Teams gesucht!" />
        <meta
          name="description"
          content="In unseren logopädischen Praxen Mannheim-Rheinau und Offenbach an der Queich sind ab sofort zwei Stellen zu besetzen."
        />
      </Head>
      <StickedNavbar links={links} />
      <Content>
        <Centered style={{ padingBottom: "2.5em" }}>
          <Headline children="Wir haben aktuell 2 Stellen zu besetzen!" />
        </Centered>

        <Grid>
          <Card>
            <Title>Offenbach an der Queich</Title>
            <p>
              In unseren logopädischen Praxen behandeln wir alle Störungsbilder.<br />
              Die Praxis in Offenbach ist ca. 6 km von Landau entfernt und in
              unmittelbarer Nähe sind immer ausreichend Parkmöglichkeiten
              vorhanden.<br />Wir suchen ab sofort oder später eine Kollegin für
              10-15 Wochenstunden (später gerne mehr).<br />Es erwarten Sie ein
              eigener Therapieraum mit computergestütztem Arbeitsplatz,
              betriebliche Altersvorsorge, Fortbildungszuschuss und eine
              überdurchschnittliche Bezahlung.<br />
              <br />
              <b>Wir freuen uns auf Ihre Bewerbung!</b>
              <br />
              <br />Logopädie Palinkas<br />Schlesienstr. 3<br />76877 Offenbach<br />
            </p>
          </Card>
          <Card>
            <Title>Mannheim Rheinau</Title>
            <p>
              In unseren logopädischen Praxen behandeln wir alle Störungsbilder.<br />
              Die Praxis in Mannheim ist sehr gut zu erreichen
              (Straßenbahnhaltestelle in ca. 100 m Entfernung bzw.
              Parkmöglichkeiten in der Nähe).<br />Wir suchen ab sofort oder
              später eine Kollegin in Teil- oder Vollzeit (gerne auch als
              fachliche Leitung).<br />Es erwarten Sie ein nettes Team, ein
              eigener Therapieraum mit computergestütztem Arbeitsplatz,
              betriebliche Altersvorsorge, Fortbildungszuschuss und eine
              überdurchschnittliche Bezahlung.<br />
              <br />
              <b>Wir freuen uns auf Ihre Bewerbung!</b>
              <br />
              <br />Logopädie Palinkas<br />Dänischer Tisch 25<br />68219
              Mannheim<br />
            </p>
          </Card>
        </Grid>
        <Container>
          Bitte senden Sie ihre Bewerbung an: <br />
          <a href="mailto:janixis@gmx.de">janixis@gmx.de</a>
          <br />
          Tel.: 0621-8995663<br />
          <a href="http://www.logopaedie-palinkas.de">
            www.logopaedie-palinkas.de
          </a>
          <Button href="/" style={{ marginTop: "2em", color: "white" }}>
            Zurück zur Startseite
          </Button>
        </Container>
      </Content>
      <Footer />
    </Site>
  </ResponsiveImage>
);



// WEBPACK FOOTER //
// /opt/build/repo/src/pages/Jobs/JobsPage.js